/**
 * Read-only YouTube Data API calls made with the host's in-memory OAuth token.
 *
 * Nothing here writes to Firestore and nothing caches the token. Only playlists
 * the user explicitly selects are imported; this module never walks the whole
 * account on its own.
 *
 * Playlist tracks are paged from playlistItems.list (max 50 per request).
 * These helpers are for live YouTube operations (import, sync, party-start
 * Add first 10). My Music persisted browsing reads Firestore, not this module.
 */
import axios from 'axios';
import { formatDuration } from './youtube';
import { parseYouTubeDurationMs } from './youtubeDuration';
import { isPlayableProviderSource } from '../catalog/playbackEligibility';
import { getYouTubeAccessToken } from './youtubeAuth';

const BASE_URL = 'https://www.googleapis.com/youtube/v3';
const PAGE_SIZE = 50;
const VIDEOS_LIST_BATCH_SIZE = 50;
/** YouTube playlists cap at 5,000 items (100 pages). 200 pages is runaway protection only. */
const MAX_PLAYLIST_PAGES = 200;

export class YouTubeAuthorizationError extends Error {
  constructor(message = 'YouTube authorization required') {
    super(message);
    this.name = 'YouTubeAuthorizationError';
  }
}

export class PlaylistIncompleteError extends Error {
  constructor(message = 'Could not load the full playlist') {
    super(message);
    this.name = 'PlaylistIncompleteError';
    this.code = 'playlist-incomplete';
  }
}

export function describePlaylistLoadFailure(error) {
  if (error instanceof YouTubeAuthorizationError || error?.name === 'YouTubeAuthorizationError') {
    return {
      code: 'youtube-auth',
      message: 'Your playlist is remembered, but Rhythmé needs YouTube access again to load its tracks.',
    };
  }

  if (error instanceof PlaylistIncompleteError || error?.code === 'playlist-incomplete') {
    return {
      code: 'playlist-incomplete',
      message: error.message || 'Could not load the full playlist. Try again.',
    };
  }

  const status = error?.response?.status;
  if (status === 401) {
    return {
      code: 'youtube-auth',
      message: 'Your playlist is remembered, but Rhythmé needs YouTube access again to load its tracks.',
    };
  }
  if (status === 404 || status === 403) {
    return {
      code: 'playlist-unavailable',
      message: 'That playlist is unavailable or was deleted.',
    };
  }

  return {
    code: 'network',
    message: 'Could not load that playlist. Check your connection and try again.',
  };
}

function authHeaders() {
  const token = getYouTubeAccessToken();
  if (!token) throw new YouTubeAuthorizationError();
  return { Authorization: `Bearer ${token}` };
}

export function normalizePlaylist(item) {
  if (!item?.id) return null;
  return {
    playlistId: item.id,
    title: item.snippet?.title || 'Untitled playlist',
    thumbnail: item.snippet?.thumbnails?.medium?.url
      || item.snippet?.thumbnails?.default?.url
      || '',
    itemCount: Number(item.contentDetails?.itemCount) || 0,
    source: 'youtube',
  };
}

/** One page of the signed-in user's own playlists. */
export async function fetchMyPlaylists({ pageToken = '' } = {}) {
  const params = {
    part: 'snippet,contentDetails',
    mine: true,
    maxResults: PAGE_SIZE,
  };
  if (pageToken) params.pageToken = pageToken;

  const response = await axios.get(`${BASE_URL}/playlists`, {
    headers: authHeaders(),
    params,
  });

  return {
    playlists: (response.data?.items || []).map(normalizePlaylist).filter(Boolean),
    nextPageToken: response.data?.nextPageToken || null,
  };
}

export function normalizePlaylistItem(item, { playlistId = null } = {}) {
  const videoId = item?.contentDetails?.videoId || item?.snippet?.resourceId?.videoId;
  if (!videoId) return null;

  const snippet = item.snippet || {};
  if (snippet.title === 'Private video' || snippet.title === 'Deleted video') return null;

  const position = Number.isInteger(snippet.position) ? snippet.position : null;

  return {
    id: videoId,
    videoId,
    title: snippet.title || 'Unknown track',
    artist: snippet.videoOwnerChannelTitle || snippet.channelTitle || '',
    thumbnail: snippet.thumbnails?.medium?.url || snippet.thumbnails?.default?.url || '',
    duration: '',
    durationMs: null,
    views: '',
    source: 'youtube-playlist',
    playlistId: playlistId || snippet.playlistId || null,
    playlistItemId: item.id || null,
    position,
  };
}

/** Library sync keeps private/deleted occurrences instead of dropping them. */
export function normalizePlaylistItemForLibrary(item, { playlistId = null } = {}) {
  const videoId = item?.contentDetails?.videoId || item?.snippet?.resourceId?.videoId;
  if (!videoId || !item?.id) return null;

  const snippet = item.snippet || {};
  let availability = 'available';
  if (snippet.title === 'Private video') availability = 'private';
  else if (snippet.title === 'Deleted video') availability = 'deleted';

  const position = Number.isInteger(snippet.position) ? snippet.position : null;

  return {
    id: videoId,
    videoId,
    title: snippet.title || 'Unknown track',
    artist: snippet.videoOwnerChannelTitle || snippet.channelTitle || '',
    thumbnail: snippet.thumbnails?.medium?.url || snippet.thumbnails?.default?.url || '',
    duration: '',
    durationMs: null,
    views: '',
    source: 'youtube-playlist',
    playlistId: playlistId || snippet.playlistId || null,
    playlistItemId: item.id || null,
    position,
    availability,
    providerChannelId: snippet.videoOwnerChannelId || snippet.channelId || null,
    providerChannelTitle: snippet.channelTitle || null,
    providerOwnerChannelTitle: snippet.videoOwnerChannelTitle || null,
    providerPublishedAt: snippet.publishedAt || null,
  };
}

function chunkIds(ids, size) {
  const batches = [];
  for (let index = 0; index < ids.length; index += size) {
    batches.push(ids.slice(index, index + size));
  }
  return batches;
}

/**
 * Duration plus embeddability from the same videos.list call.
 * Failure degrades to leaving duration/embeddable unset rather than blocking.
 * videos.list accepts at most 50 ids per request; large playlists are batched.
 */
async function attachDurations(tracks) {
  const ids = [];
  const seen = new Set();
  tracks.forEach((track) => {
    if (!track?.id || seen.has(track.id)) return;
    seen.add(track.id);
    ids.push(track.id);
  });
  if (ids.length === 0) return tracks;

  try {
    const byId = new Map();
    const checkedAt = new Date();
    const batches = chunkIds(ids, VIDEOS_LIST_BATCH_SIZE);
    for (let index = 0; index < batches.length; index += 1) {
      // Sequential: each videos.list is 1 quota unit; parallelizing does not
      // reduce quota and can trip per-user rate limits on large playlists.
      // eslint-disable-next-line no-await-in-loop
      const response = await axios.get(`${BASE_URL}/videos`, {
        headers: authHeaders(),
        params: {
          part: 'contentDetails,status',
          id: batches[index].join(','),
        },
      });
      (response.data?.items || []).forEach((item) => {
        byId.set(item.id, {
          duration: item.contentDetails?.duration,
          embeddable: item.status?.embeddable,
        });
      });
    }

    return tracks.map((track) => {
      const meta = byId.get(track.id);
      if (!meta) {
        return {
          ...track,
          embeddableCheckedAt: checkedAt,
          playbackEligibilityReason: 'not_in_videos_list',
        };
      }
      const next = {
        ...track,
        embeddableCheckedAt: checkedAt,
      };
      if (meta.embeddable === true || meta.embeddable === false) {
        next.embeddable = meta.embeddable;
        next.playbackEligibilityReason = meta.embeddable
          ? 'embeddable'
          : 'not_embeddable';
      }
      if (meta.duration) {
        next.duration = formatDuration(meta.duration);
        next.durationMs = parseYouTubeDurationMs(meta.duration);
      }
      return next;
    });
  } catch {
    return tracks;
  }
}

export async function attachVideoMetadata(tracks) {
  return attachDurations(tracks);
}

export async function fetchPlaylistMetadata(playlistId) {
  if (!playlistId) {
    const error = new Error('Playlist unavailable');
    error.code = 'playlist-unavailable';
    error.response = { status: 404 };
    throw error;
  }

  const response = await axios.get(`${BASE_URL}/playlists`, {
    headers: authHeaders(),
    params: {
      part: 'snippet,contentDetails,status',
      id: playlistId,
      maxResults: 1,
    },
  });

  const item = Array.isArray(response.data?.items) ? response.data.items[0] : null;
  if (!item?.id) {
    const error = new Error('Playlist unavailable');
    error.code = 'playlist-unavailable';
    error.response = { status: 404 };
    throw error;
  }

  return {
    playlistId: item.id,
    title: item.snippet?.title || 'Untitled playlist',
    description: item.snippet?.description ?? null,
    thumbnail: item.snippet?.thumbnails?.medium?.url
      || item.snippet?.thumbnails?.default?.url
      || '',
    itemCount: Number(item.contentDetails?.itemCount) || 0,
    privacyStatus: item.status?.privacyStatus ?? null,
    channelId: item.snippet?.channelId ?? null,
    etag: item.etag || null,
    source: 'youtube',
  };
}

export async function fetchPlaylistItemsPage(playlistId, pageToken = '') {
  const params = {
    part: 'snippet,contentDetails',
    playlistId,
    maxResults: PAGE_SIZE,
  };
  if (pageToken) params.pageToken = pageToken;

  const response = await axios.get(`${BASE_URL}/playlistItems`, {
    headers: authHeaders(),
    params,
  });

  return {
    items: Array.isArray(response.data?.items) ? response.data.items : [],
    nextPageToken: response.data?.nextPageToken || null,
  };
}

function collectUsableTracks(items, playlistId, seenItemIds) {
  const tracks = [];
  let skipped = 0;

  items.forEach((item) => {
    const itemId = item?.id;
    if (itemId && seenItemIds.has(itemId)) {
      skipped += 1;
      return;
    }
    if (itemId) seenItemIds.add(itemId);

    const track = normalizePlaylistItem(item, { playlistId });
    if (track) tracks.push(track);
    else skipped += 1;
  });

  return { tracks, skipped };
}

async function paginatePlaylistItems(playlistId, { stopAfterUsable = null } = {}) {
  const seenItemIds = new Set();
  const tracks = [];
  let skipped = 0;
  let pageToken = '';
  let pages = 0;

  do {
    pages += 1;
    if (pages > MAX_PLAYLIST_PAGES) {
      throw new PlaylistIncompleteError(
        `Playlist pagination exceeded ${MAX_PLAYLIST_PAGES} pages without finishing.`,
      );
    }

    // Sequential page fetches: each pageToken is only known after the previous
    // response, and a later-page failure must not look like a complete playlist.
    // eslint-disable-next-line no-await-in-loop
    const page = await fetchPlaylistItemsPage(playlistId, pageToken);
    const collected = collectUsableTracks(page.items, playlistId, seenItemIds);
    tracks.push(...collected.tracks);
    skipped += collected.skipped;
    pageToken = page.nextPageToken || '';

    if (Number.isInteger(stopAfterUsable) && tracks.length >= stopAfterUsable) {
      break;
    }
  } while (pageToken);

  return { tracks, skipped };
}

/**
 * Every accessible track in playlist order. Private/deleted/missing items are
 * skipped rather than failing the playlist.
 */
export async function fetchAllPlaylistTracks(playlistId, { withDurations = true } = {}) {
  if (!playlistId) return { tracks: [], skipped: 0 };
  authHeaders();

  const { tracks, skipped } = await paginatePlaylistItems(playlistId);
  const withMeta = withDurations ? await attachDurations(tracks) : tracks;
  return { tracks: withMeta, skipped };
}

/**
 * Enough usable tracks to seed the queue (Add first 10). Stops paging once
 * `limit` playable items are in hand so a 203-song playlist is not fully
 * downloaded just to append ten songs.
 */
export async function fetchPlaylistSeedTracks(playlistId, { limit = 10, withDurations = true } = {}) {
  if (!playlistId) return { tracks: [] };
  authHeaders();

  const max = Number.isInteger(limit) && limit > 0 ? limit : 10;
  const { tracks } = await paginatePlaylistItems(playlistId, { stopAfterUsable: max });
  const selected = tracks.slice(0, max);
  const withMeta = withDurations ? await attachDurations(selected) : selected;
  return {
    tracks: withMeta.filter((track) => isPlayableProviderSource({
      ...track,
      availability: track.availability || 'available',
      providerAssetId: track.videoId || track.id,
    })),
  };
}

/** @deprecated Use fetchAllPlaylistTracks. Kept so older callers still load every page. */
export async function fetchPlaylistTracks(playlistId, options = {}) {
  return fetchAllPlaylistTracks(playlistId, options);
}
